/* Methodic Hero Ribbon — A1 Liquid Wake · Wide Composition V4.
 * Ported verbatim from Hero_Ribbon_A1_Wide_V4.html (variant "a").
 * A1 restore: original geometry/dynamic drift; flowmap-style dissipative mouse velocity field.
 * Inspired by OGL mouse-flowmap architecture (concept only), not a copy of OGL code.
 * Does not require WebGL or third-party libraries.
 * Only change from the prototype: wrapped in mount()/destroy() for React lifecycle cleanup. */

type Band = {
  x0: number; y0: number; x1: number; y1: number; width: number; sweep: number; wave: number;
  phase: number; curl: number; col: [number, number, number]; count: number;
};

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const rgba = (r: number, g: number, b: number, a: number) => `rgba(${r},${g},${b},${clamp(a, 0, 1)})`;

// V4: composition-only geometry tuning. Preserve the A1 weave and V3 liquid velocity field.
const RIBBON_COMPOSITION = { pivotX: 1020, scaleX: 1.19, offsetX: -62, widthFactor: 1.10 };

const bandsA: Band[] = [
  { x0: 610, y0: 1005, x1: 1670, y1: -195, width: 185, sweep: 100, wave: 92, phase: -0.05, curl: 25, col: [17, 90, 230], count: 72 },
  { x0: 725, y0: 1010, x1: 1525, y1: -230, width: 132, sweep: -68, wave: 40, phase: 1.75, curl: 23, col: [125, 124, 218], count: 49 },
  { x0: 710, y0: 960, x1: 1635, y1: -180, width: 79, sweep: 40, wave: -32, phase: 3.2, curl: 14, col: [115, 165, 224], count: 29 },
];

const GRID_X = 60;
const GRID_Y = 38;
const N = GRID_X * GRID_Y;

export function mountHeroRibbon(
  stage: HTMLElement,
  canvas: HTMLCanvasElement,
  events: HTMLElement = stage,
  opts: { mirror?: boolean; tone?: 'default' | 'onBlue' } = {},
): () => void {
  const mirror = !!opts.mirror;
  const onBlue = opts.tone === 'onBlue';
  // On the blue CTA field the three bands become white tints so they read on Signal Blue.
  const ON_BLUE: [number, number, number][] = [[255, 255, 255], [222, 228, 255], [196, 214, 255]];
  const maybeCtx = canvas.getContext('2d', { alpha: true });
  if (!maybeCtx) return () => {};
  const ctx: CanvasRenderingContext2D = maybeCtx;
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  let w = 0; let h = 0; let dpr = 1; let active = true; let raf = 0; let last = 0; let elapsed = 0;
  let destroyed = false;

  let U = new Float32Array(N); let V = new Float32Array(N); let D = new Float32Array(N); let E = new Float32Array(N);
  let UU = new Float32Array(N); let VV = new Float32Array(N); let DD = new Float32Array(N); let EE = new Float32Array(N);
  const splats: { x: number; y: number; dx: number; dy: number }[] = [];
  const motion = { lastX: 0, lastY: 0, lastTime: 0, hasPrev: false };
  let fieldEnergy = 0;
  const idx = (x: number, y: number) => y * GRID_X + x;

  function sample(arr: Float32Array, gxIn: number, gyIn: number) {
    const gx = clamp(gxIn, 0, GRID_X - 1.001); const gy = clamp(gyIn, 0, GRID_Y - 1.001);
    const x = Math.floor(gx); const y = Math.floor(gy); const fx = gx - x; const fy = gy - y; const k = idx(x, y);
    const a = arr[k] * (1 - fx) + arr[k + 1] * fx;
    const b = arr[k + GRID_X] * (1 - fx) + arr[k + GRID_X + 1] * fx;
    return a * (1 - fy) + b * fy;
  }
  function clearFlow() {
    U.fill(0); V.fill(0); D.fill(0); E.fill(0); UU.fill(0); VV.fill(0); DD.fill(0); EE.fill(0);
    fieldEnergy = 0; splats.length = 0; motion.hasPrev = false;
  }
  function injectStroke(x: number, y: number, dx: number, dy: number) {
    const len = Math.hypot(dx, dy); if (len < 1.2) return;
    const mag = Math.min(len, 38); const gain = mag / len;
    splats.push({ x, y, dx: dx * gain, dy: dy * gain });
    if (splats.length > 16) splats.shift();
  }
  function stepFlow(dt: number) {
    if (!w || !h) return;
    const cellX = w / (GRID_X - 1); const cellY = h / (GRID_Y - 1);
    const velDecay = Math.exp(-dt * 2.65); const dispDecay = Math.exp(-dt * 0.92);
    let energy = 0;
    for (let y = 1; y < GRID_Y - 1; y += 1) {
      for (let x = 1; x < GRID_X - 1; x += 1) {
        const k = idx(x, y);
        const bx = x - (U[k] * dt * 0.72) / cellX; const by = y - (V[k] * dt * 0.72) / cellY;
        const vx = sample(U, bx, by); const vy = sample(V, bx, by);
        const smoothU = (U[k - 1] + U[k + 1] + U[k - GRID_X] + U[k + GRID_X]) * 0.25;
        const smoothV = (V[k - 1] + V[k + 1] + V[k - GRID_X] + V[k + GRID_X]) * 0.25;
        UU[k] = (vx * 0.94 + smoothU * 0.06) * velDecay;
        VV[k] = (vy * 0.94 + smoothV * 0.06) * velDecay;
        DD[k] = clamp(sample(D, bx, by) * dispDecay + UU[k] * dt * 0.72, -42, 42);
        EE[k] = clamp(sample(E, bx, by) * dispDecay + VV[k] * dt * 0.72, -42, 42);
        energy += Math.abs(UU[k]) + Math.abs(VV[k]);
      }
    }
    [U, UU] = [UU, U]; [V, VV] = [VV, V]; [D, DD] = [DD, D]; [E, EE] = [EE, E];
    UU.fill(0); VV.fill(0); DD.fill(0); EE.fill(0);
    splats.forEach((s) => {
      const rx = 142; const ry = 138;
      const xs = Math.max(1, Math.floor((s.x - 2 * rx) / cellX)); const xe = Math.min(GRID_X - 2, Math.ceil((s.x + 2 * rx) / cellX));
      const ys = Math.max(1, Math.floor((s.y - 2 * ry) / cellY)); const ye = Math.min(GRID_Y - 2, Math.ceil((s.y + 2 * ry) / cellY));
      const speed = Math.hypot(s.dx, s.dy);
      const direction = Math.sign(s.dx * 0.76 + s.dy * 0.38) || 1;
      for (let iy = ys; iy <= ye; iy += 1) {
        for (let ix = xs; ix <= xe; ix += 1) {
          const px = ix * cellX - s.x; const py = iy * cellY - s.y;
          const r2 = (px * px) / (rx * rx) + (py * py) / (ry * ry);
          if (r2 <= 5) {
            const fall = Math.exp(-r2 * 1.3);
            const k = idx(ix, iy);
            const swirl = Math.min(1, Math.sqrt(r2)) * 0.19 * direction;
            const tangX = (-py / ry) * speed * swirl;
            const tangY = (px / rx) * speed * swirl;
            U[k] += (s.dx * 0.92 + tangX) * fall * 4.65;
            V[k] += (s.dy * 0.92 + tangY) * fall * 4.65;
          }
        }
      }
    });
    splats.length = 0; fieldEnergy = energy;
  }
  function flowPoint(point: [number, number]): [number, number] {
    if (fieldEnergy < 0.002 || !w || !h) return point;
    const [px, py] = point;
    const fx = (px * (GRID_X - 1)) / w; const fy = (py * (GRID_Y - 1)) / h;
    return [px + sample(D, fx, fy), py + sample(E, fx, fy)];
  }
  const mobile = () => w < 700;
  const ribbonExpansion = () => clamp((w - 680) / 360, 0, 1);
  function fit(x: number, y: number): [number, number] {
    const factor = ribbonExpansion();
    const c = RIBBON_COMPOSITION;
    const composedX = c.pivotX + (x - c.pivotX) * (1 + (c.scaleX - 1) * factor) + c.offsetX * factor;
    return [(composedX * w) / 1440, (y * h) / 833];
  }
  function fieldA(band: Band, u: number, p: number, time: number) {
    const bend = Math.sin(Math.PI * p);
    let x = band.x0 + (band.x1 - band.x0) * p + Math.sin(p * 6.25 + band.phase) * band.sweep * bend;
    let y = band.y0 + (band.y1 - band.y0) * p + Math.sin(p * 5.9 + band.phase) * band.wave * bend;
    const local = 30 * Math.sin(p * Math.PI * 2.2 + band.phase + 0.5 * time) * bend;
    const width = band.width * (1 + (RIBBON_COMPOSITION.widthFactor - 1) * ribbonExpansion())
      * (0.60 + 0.37 * Math.sin(p * 2.35 + band.phase) ** 2);
    const crossingSoftness = 1 - 0.075 * Math.exp(-(((p - 0.54) / 0.16) ** 2));
    const twist = Math.cos(p * 5.5 + band.phase * 0.8 + time * 0.11) * crossingSoftness;
    const nx = 0.77; const ny = 0.62;
    x += u * width * nx * twist + u * width * 0.28 * Math.sin(p * 5 + band.phase) * (-ny);
    y += u * width * ny * twist + u * width * 0.28 * Math.sin(p * 5 + band.phase) * nx;
    y += local + (u * u) * band.curl * Math.sin(p * 7.8 + band.phase + time * 0.12) * bend;
    return fit(x, y);
  }
  function drawBand(band: Band, time: number) {
    const n = mobile() ? Math.round(band.count * 0.60) : band.count;
    const nstep = mobile() ? 115 : 158;
    for (let i = 0; i < n; i += 1) {
      const u = (i / (n - 1)) * 2 - 1;
      const shade = 0.55 + 0.45 * Math.abs(u);
      const waveHighlight = 0.65 + 0.35 * Math.sin((u * 2.5 + time * 0.05 + band.phase) * 2.4) ** 2;
      const alpha = 0.32 * shade * waveHighlight + Math.abs(u) ** 7 * 0.10;
      ctx.beginPath();
      for (let s = 0; s <= nstep; s += 1) {
        const [px, py] = flowPoint(fieldA(band, u, s / nstep, time));
        if (!s) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.lineWidth = (Math.abs(u) > 0.91 ? 1.03 : 0.78) * Math.min(1.3, w / 1440 + 0.2);
      const col = onBlue ? ON_BLUE[bandsA.indexOf(band)] ?? band.col : band.col;
      ctx.strokeStyle = rgba(col[0], col[1], col[2], onBlue ? alpha * 1.15 : alpha);
      ctx.stroke();
    }
  }
  function draw(time: number) {
    if (!w || !h) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    // Mirror: flips the composition horizontally (top-right → bottom-left becomes top-left → bottom-right).
    if (mirror) ctx.setTransform(-dpr, 0, 0, dpr, w * dpr, 0);
    for (let b = bandsA.length - 1; b >= 0; b -= 1) drawBand(bandsA[b], time);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  function resize() {
    const box = stage.getBoundingClientRect(); w = box.width; h = box.height;
    dpr = Math.min(window.devicePixelRatio || 1, mobile() ? 1.35 : 1.7);
    canvas.width = Math.max(1, Math.round(w * dpr)); canvas.height = Math.max(1, Math.round(h * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); clearFlow(); draw(elapsed);
  }
  function tick(now: number) {
    raf = 0; if (!active || destroyed) return;
    const dtMs = last ? Math.min(50, now - last) : 16; last = now;
    if (!mq.matches) { elapsed += dtMs / 1000; stepFlow(dtMs / 1000); }
    draw(elapsed);
    if (!mq.matches) raf = requestAnimationFrame(tick);
  }
  function start() { if (destroyed || raf || mq.matches || !active) return; last = 0; raf = requestAnimationFrame(tick); }
  function pause() { if (raf) cancelAnimationFrame(raf); raf = 0; last = 0; }
  function pointermove(e: PointerEvent) {
    if (mq.matches || e.pointerType === 'touch') return;
    const r = stage.getBoundingClientRect(); const rawX = e.clientX - r.left; const y = e.clientY - r.top;
    // Work in the ribbon's own (unmirrored) space so the wake follows the visible lines.
    const x = mirror ? w - rawX : rawX;
    const now = performance.now();
    if (motion.hasPrev) {
      const gap = now - motion.lastTime;
      if (gap > 0 && gap < 150 && x > Math.max(500, w * 0.38)) {
        const dx = x - motion.lastX; const dy = y - motion.lastY;
        const count = Math.min(8, Math.max(1, Math.ceil(Math.hypot(dx, dy) / 30)));
        for (let j = 1; j <= count; j += 1) {
          const t = j / count;
          injectStroke(motion.lastX + dx * t, motion.lastY + dy * t, dx / count, dy / count);
        }
      }
    }
    motion.lastX = x; motion.lastY = y; motion.lastTime = now; motion.hasPrev = true;
  }
  const onLeave = () => { motion.hasPrev = false; };
  const onVisibility = () => {
    active = !document.hidden && stage.getBoundingClientRect().bottom > 0;
    if (active) start(); else pause();
  };
  const onMotionPref = () => { pause(); clearFlow(); elapsed = 0; draw(0); start(); };

  events.addEventListener('pointermove', pointermove, { passive: true });
  events.addEventListener('pointerleave', onLeave, { passive: true });
  window.addEventListener('blur', onLeave);
  const io = new IntersectionObserver((es) => {
    active = !!es[0]?.isIntersecting && !document.hidden;
    if (active) start(); else pause();
  }, { threshold: 0.01 });
  io.observe(stage);
  document.addEventListener('visibilitychange', onVisibility);
  mq.addEventListener('change', onMotionPref);
  const ro = new ResizeObserver(resize); ro.observe(stage);
  resize(); start();

  return () => {
    destroyed = true;
    pause();
    io.disconnect(); ro.disconnect();
    events.removeEventListener('pointermove', pointermove);
    events.removeEventListener('pointerleave', onLeave);
    window.removeEventListener('blur', onLeave);
    document.removeEventListener('visibilitychange', onVisibility);
    mq.removeEventListener('change', onMotionPref);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };
}